import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Warning,
  CheckCircle,
  Info,
  Refresh,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { supabase } from '../lib/supabase';
import { formatCurrency, formatDataUsage } from '../utils/formatters';

interface ThresholdSummary {
  threshold_30_days_count: number;
  threshold_30_days_estimated_cost: number;
  threshold_15_days_count: number;
  threshold_15_days_estimated_cost: number;
  threshold_7_days_count: number;
  threshold_7_days_estimated_cost: number;
  total_estimated_cost: number;
  monthly_management_fee: number;
  required_wallet_balance: number;
  current_wallet_balance: number;
  funding_gap: number;
}

interface ThresholdDetail {
  sim_id: string;
  msisdn: string;
  cluster_name: string;
  organization_name?: string; // Added for superadmin view
  balance_type: string;
  current_balance: number;
  threshold_value: number;
  days_until_threshold: number;
  daily_consumption: number;
  topup_cost: number;
  product_name: string;
}

interface ThresholdMonitoringDashboardProps {
  organizationId: string | null; // null for superadmin (all organizations)
  onRefresh?: () => void;
}

export function ThresholdMonitoringDashboard({ organizationId, onRefresh }: ThresholdMonitoringDashboardProps) {
  const [summary, setSummary] = useState<ThresholdSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedThreshold, setSelectedThreshold] = useState<number | null>(null);
  const [details, setDetails] = useState<ThresholdDetail[]>([]);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await supabase
        .rpc('get_threshold_monitoring_summary', { p_organization_id: organizationId });

      if (error) throw error;
      
      if (data && data.length > 0) {
        setSummary(data[0]);
      } else {
        setSummary({
          threshold_30_days_count: 0,
          threshold_30_days_estimated_cost: 0,
          threshold_15_days_count: 0,
          threshold_15_days_estimated_cost: 0,
          threshold_7_days_count: 0,
          threshold_7_days_estimated_cost: 0,
          total_estimated_cost: 0,
          monthly_management_fee: 0,
          required_wallet_balance: 0,
          current_wallet_balance: 0,
          funding_gap: 0,
        });
      }
    } catch (err: any) {
      console.error('Error fetching threshold summary:', err);
      setError(err.message || 'Failed to load threshold monitoring data');
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  const fetchDetails = async (thresholdDays: number) => {
    try {
      setDetailsLoading(true);
      
      const { data, error } = await supabase
        .rpc('get_threshold_monitoring_details', { 
          p_organization_id: organizationId,
          p_threshold_days: thresholdDays
        });

      if (error) throw error;
      
      setDetails(data || []);
      setSelectedThreshold(thresholdDays);
      setDetailsOpen(true);
    } catch (err: any) {
      console.error('Error fetching threshold details:', err);
      setError(err.message || 'Failed to load threshold details');
    } finally {
      setDetailsLoading(false);
    }
  };

  useEffect(() => {
    // Always fetch summary - organizationId can be null for superadmin
    fetchSummary();
  }, [organizationId, fetchSummary]);

  const handleRefresh = () => {
    fetchSummary();
    if (onRefresh) onRefresh();
  };

  const getThresholdColor = (days: number) => {
    switch (days) {
      case 30: return 'info';
      case 15: return 'warning';
      case 7: return 'error';
      default: return 'default';
    }
  };

  const getThresholdIcon = (days: number) => {
    switch (days) {
      case 30: return <Info />;
      case 15: return <Warning />;
      case 7: return <Warning />;
      default: return <Info />;
    }
  };

  const getThresholdDescription = (days: number) => {
    switch (days) {
      case 30: return 'Planning Zone';
      case 15: return 'Action Required';
      case 7: return 'Urgent';
      default: return 'Unknown';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!summary) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No threshold monitoring data available.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
          <TrendingUp sx={{ mr: 1 }} />
          SIM Threshold Monitoring
        </Typography>
        <Button variant="outlined" size="small" onClick={handleRefresh}>
          Refresh
        </Button>
      </Box>

      {/* Threshold Alert Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* 30 Days Threshold */}
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card 
            sx={{ 
              height: '100%',
              cursor: summary.threshold_30_days_count > 0 ? 'pointer' : 'default',
              transition: 'all 0.2s ease-in-out',
              '&:hover': summary.threshold_30_days_count > 0 ? {
                transform: 'translateY(-2px)',
                boxShadow: 3,
                bgcolor: 'action.hover'
              } : {}
            }}
            onClick={summary.threshold_30_days_count > 0 ? () => fetchDetails(30) : undefined}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="h4" color="info.main">
                    {summary.threshold_30_days_count}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    SIMs within 30 days
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Est: R{formatCurrency(summary.threshold_30_days_estimated_cost)}
                  </Typography>
                  {summary.threshold_30_days_count > 0 && (
                    <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                      Click to view details
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  <Chip
                    icon={getThresholdIcon(30)}
                    label={getThresholdDescription(30)}
                    color={getThresholdColor(30) as any}
                    size="small"
                    sx={{ mb: 1 }}
                  />
                  {summary.threshold_30_days_count > 0 && (
                    <Visibility color="action" fontSize="small" />
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* 15 Days Threshold */}
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card 
            sx={{ 
              height: '100%',
              cursor: summary.threshold_15_days_count > 0 ? 'pointer' : 'default',
              transition: 'all 0.2s ease-in-out',
              '&:hover': summary.threshold_15_days_count > 0 ? {
                transform: 'translateY(-2px)',
                boxShadow: 3,
                bgcolor: 'action.hover'
              } : {}
            }}
            onClick={summary.threshold_15_days_count > 0 ? () => fetchDetails(15) : undefined}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="h4" color="warning.main">
                    {summary.threshold_15_days_count}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    SIMs within 15 days
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Est: R{formatCurrency(summary.threshold_15_days_estimated_cost)}
                  </Typography>
                  {summary.threshold_15_days_count > 0 && (
                    <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                      Click to view details
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  <Chip
                    icon={getThresholdIcon(15)}
                    label={getThresholdDescription(15)}
                    color={getThresholdColor(15) as any}
                    size="small"
                    sx={{ mb: 1 }}
                  />
                  {summary.threshold_15_days_count > 0 && (
                    <Visibility color="action" fontSize="small" />
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* 7 Days Threshold */}
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card 
            sx={{ 
              height: '100%',
              cursor: summary.threshold_7_days_count > 0 ? 'pointer' : 'default',
              transition: 'all 0.2s ease-in-out',
              '&:hover': summary.threshold_7_days_count > 0 ? {
                transform: 'translateY(-2px)',
                boxShadow: 3,
                bgcolor: 'action.hover'
              } : {}
            }}
            onClick={summary.threshold_7_days_count > 0 ? () => fetchDetails(7) : undefined}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="h4" color="error.main">
                    {summary.threshold_7_days_count}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    SIMs within 7 days
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Est: R{formatCurrency(summary.threshold_7_days_estimated_cost)}
                  </Typography>
                  {summary.threshold_7_days_count > 0 && (
                    <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                      Click to view details
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  <Chip
                    icon={getThresholdIcon(7)}
                    label={getThresholdDescription(7)}
                    color={getThresholdColor(7) as any}
                    size="small"
                    sx={{ mb: 1 }}
                  />
                  {summary.threshold_7_days_count > 0 && (
                    <Visibility color="action" fontSize="small" />
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Financial Summary */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <Info sx={{ mr: 1 }} />
            Financial Summary
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Total Estimated Cost
              </Typography>
              <Typography variant="h6">
                R{formatCurrency(summary.total_estimated_cost)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Monthly Management Fee
              </Typography>
              <Typography variant="h6">
                R{formatCurrency(summary.monthly_management_fee)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Required Wallet Balance
              </Typography>
              <Typography variant="h6">
                R{formatCurrency(summary.required_wallet_balance)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Current Wallet Balance
              </Typography>
              <Typography 
                variant="h6" 
                color={summary.current_wallet_balance < summary.required_wallet_balance ? 'error' : 'success.main'}
              >
                R{formatCurrency(summary.current_wallet_balance)}
              </Typography>
            </Grid>
          </Grid>
          
          {summary.funding_gap > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Funding Gap:</strong> R{formatCurrency(summary.funding_gap)} additional funds needed to cover estimated costs and fees.
              </Typography>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Details Modal */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              SIMs within {selectedThreshold} days of threshold
            </Typography>
            <IconButton onClick={() => setDetailsOpen(false)}>
              <VisibilityOff />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {detailsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>SIM</TableCell>
                    {organizationId === null && <TableCell>Organization</TableCell>}
                    <TableCell>Cluster</TableCell>
                    <TableCell>Balance Type</TableCell>
                    <TableCell>Current Balance</TableCell>
                    <TableCell>Threshold</TableCell>
                    <TableCell>Days Until Threshold</TableCell>
                    <TableCell>Daily Consumption</TableCell>
                    <TableCell>Top-up Cost</TableCell>
                    <TableCell>Product</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {details.map((detail, index) => (
                    <TableRow key={index}>
                      <TableCell>{detail.msisdn || 'N/A'}</TableCell>
                      {organizationId === null && <TableCell>{detail.organization_name || 'N/A'}</TableCell>}
                      <TableCell>{detail.cluster_name || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip 
                          label={detail.balance_type} 
                          size="small"
                          color={detail.balance_type === 'data' ? 'primary' : 'secondary'}
                        />
                      </TableCell>
                      <TableCell>
                        {detail.balance_type === 'data' 
                          ? formatDataUsage(detail.current_balance)
                          : `R${formatCurrency(detail.current_balance)}`
                        }
                      </TableCell>
                      <TableCell>
                        {detail.balance_type === 'data' 
                          ? formatDataUsage(detail.threshold_value)
                          : `R${formatCurrency(detail.threshold_value)}`
                        }
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={`${detail.days_until_threshold.toFixed(1)} days`}
                          size="small"
                          color={detail.days_until_threshold <= 7 ? 'error' : 
                                 detail.days_until_threshold <= 15 ? 'warning' : 'info'}
                        />
                      </TableCell>
                      <TableCell>
                        {detail.balance_type === 'data' 
                          ? formatDataUsage(detail.daily_consumption)
                          : `R${formatCurrency(detail.daily_consumption)}`
                        }
                      </TableCell>
                      <TableCell>R{formatCurrency(detail.topup_cost)}</TableCell>
                      <TableCell>{detail.product_name || 'N/A'}</TableCell>
                    </TableRow>
                  ))}
                  {details.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={organizationId === null ? 10 : 9} align="center">
                        No SIMs found at this threshold level.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 